import React, { memo, useMemo } from "react"
import Card from "@material-ui/core/Card"
import CardHeader from "@material-ui/core/CardHeader"
import CardContent from "@material-ui/core/CardContent"
import { Doughnut } from "react-chartjs-2"
import BibleBookList from "../../data/BookChapterList.json"
import { getTranslation } from "../../scripts/getTranslation"

import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles({
  main: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    textAlign: "center",
    marginBottom: "8px",
    maxHeight: "fit-content",
  },
  card: {
    maxHeight: "150px",
    margin: "5px",
  },
  leftCard: {
    marginRight: "8px",
  },
  top5: {
    flex: 1,
    textAlign: "center",
    fontSize: "75%",
    padding: 0,
  },
  cardTitle: {
    margin: "0",
    textDecoration: "underline",
    fontWeight: "bold",
    fontSize: "80%",
    padding: "2px",
    lineHeight: "150%",
  },
  top5List: {
    padding: "1px",
    margin: 1,
    fontSize: "100%",
  },
  cardContent: {
    margin: 0,
    padding: 0,
    fontSize: "11px",
    lineHeight: "13px",
  },
  backgroundColor: {
    backgroundColor: "white",
    border: "solid grey 1px",
  },
  red: {
    color: "#d60000",
  },
  green: {
    color: "#006400",
  },
  blue: {
    color: "darkblue",
  },
  topSources: {
    flex: "1 100px",
  },
  topWords: {
    flex: "1 100px",
  },
  colorComparison: {
    flex: "1 1 300px",
  },
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: true,
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 0,
      bottom: 50,
    },
  },
  legend: {
    position: "right",
    labels: {
      fontSize: 9,
      boxWidth: 40,
    },
    // display: false
  },
}

const capitalize = word => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

const SegmentData = memo(
  ({ translations, language, currentBook, repeatedWords, colors, sources }) => {
    const classes = useStyles()

    const getTopSources = obj => {
      const sourceNames = Object.keys(obj)
      let sourcesArray = sourceNames.map(sourceName => {
        return {
          key: sourceName,
          val: obj[sourceName].words,
          color: obj[sourceName].color,
        }
      })

      sourcesArray = sourcesArray.sort(function(a, b) {
        if (a.val > b.val) {
          return -1
        } else if (a.val < b.val) {
          return 1
        } else if (a.val === b.val) {
          return 0
        }
        return 0
      })
      const top5Sources = []
      let count = 0
      sourcesArray.forEach(source => {
        if (source.key !== "The Narrator" && count < 5) {
          top5Sources.push({
            name: source.key,
            words: source.val,
            color: source.color,
          })
          count += 1
        } else return
      })
      return top5Sources
    }

    const createColorData = (obj, translations, language) => {
      // const labels = Object.keys(obj)
      // labels.pop()
      const values = ["black", "red", "green", "blue"].map(l => obj[l])
      return {
        labels: [
          `${getTranslation(
            translations,
            language,
            "UI",
            "source_narrator"
          )} (${Math.max(Math.round((values[0] / obj.total) * 1000) / 10)}%)`,
          `${getTranslation(
            translations,
            language,
            "UI",
            "source_god"
          )} (${Math.max(Math.round((values[1] / obj.total) * 1000) / 10)}%)`,
          `${getTranslation(
            translations,
            language,
            "UI",
            "source_lead"
          )} (${Math.max(Math.round((values[2] / obj.total) * 1000) / 10)}%)`,
          `${getTranslation(
            translations,
            language,
            "UI",
            "source_other"
          )} (${Math.max(Math.round((values[3] / obj.total) * 1000) / 10)}%)`,
        ],
        datasets: [
          {
            data: [values[0], values[1], values[2], values[3]],
            backgroundColor: [
              "grey",
              "#ffadad",
              "#b9f8b9",
              "rgb(142, 227, 255)",
            ],
            hoverBackgroundColor: ["black", "#d60000", "#006400", "darkblue"],
          },
        ],
        options: {
          direction: language === "ARB" ? "rtl" : "ltr",
        },
      }
    }

    const colorData = useMemo(
      () => createColorData(colors, translations, language),
      [colors, translations, language]
    )

    const letter = Object.keys(BibleBookList).slice(44, 65)
    if (letter.indexOf(currentBook) === -1) {
      return (
        <div
          className={classes.main}
          // style={language === "ARB" ? { direction: "rtl" } : {}}
        >
          <Card
            className={`${classes.backgroundColor} ${classes.card} ${
              classes.colorComparison
            }`}
            style={language === "ARB" ? { direction: "rtl" } : {}}
          >
            <CardHeader
              title={getTranslation(
                translations,
                language,
                "UI",
                "source_role_comparison"
              )}
              disableTypography={true}
              className={classes.cardTitle}
            />
            <CardContent className={`${classes.cardContent} ${classes.chart}`}>
              <Doughnut
                data={colorData}
                options={chartOptions}
                height={null}
                width={null}
              />
            </CardContent>
          </Card>
          <Card
            className={`${classes.top5} ${classes.leftCard} ${
              classes.backgroundColor
            } ${classes.card}  ${classes.topSources}`}
            style={language === "ARB" ? { direction: "rtl" } : {}}
          >
            <CardHeader
              title={getTranslation(
                translations,
                language,
                "UI",
                "most_frequent_sources"
              )}
              disableTypography={true}
              className={classes.cardTitle}
            />
            <CardContent className={classes.cardContent}>
              {getTopSources(sources).map((source, index) => (
                <p
                  className={`${classes.top5List} ${classes[source.color]} ${
                    classes.topWords
                  }`}
                  key={index}
                >
                  {getTranslation(
                    translations,
                    language,
                    "Sources",
                    source.name
                  )}{" "}
                  ({source.words})
                </p>
              ))}
            </CardContent>
          </Card>
          <Card
            className={`${classes.top5} ${classes.backgroundColor} ${
              classes.card
            } ${classes.topWords}`}
            style={language === "ARB" ? { direction: "rtl" } : {}}
          >
            <CardHeader
              title={getTranslation(
                translations,
                language,
                "UI",
                "most_frequent_words"
              )}
              disableTypography={true}
              className={classes.cardTitle}
            />
            <CardContent className={classes.cardContent}>
              <p className={classes.top5List}>{capitalize(repeatedWords[0])}</p>
              <p className={classes.top5List}>{capitalize(repeatedWords[2])}</p>
              <p className={classes.top5List}>{capitalize(repeatedWords[1])}</p>
              <p className={classes.top5List}>{capitalize(repeatedWords[3])}</p>
              <p className={classes.top5List}>{capitalize(repeatedWords[4])}</p>
            </CardContent>
          </Card>
        </div>
      )
    } else {
      return (
        <div className={classes.main}>
          <Card
            className={`${classes.top5} ${classes.backgroundColor} ${
              classes.card
            } ${classes.topWords}`}
          >
            <CardHeader
              title={getTranslation(
                translations,
                language,
                "UI",
                "most_frequent_words"
              )}
              disableTypography={true}
              className={classes.cardTitle}
            />
            <CardContent className={classes.cardContent}>
              <p className={classes.top5List}> {repeatedWords[0]} </p>
              <p className={classes.top5List}> {repeatedWords[2]} </p>
              <p className={classes.top5List}> {repeatedWords[1]} </p>
              <p className={classes.top5List}> {repeatedWords[3]} </p>
              <p className={classes.top5List}> {repeatedWords[4]} </p>
            </CardContent>
          </Card>
        </div>
      )
    }
  }
)

export default SegmentData
